import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  TheoryLevelRequest,
  SkillTestQuestion,
} from '../../../models/skill-learning.model';
import {
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_LEVEL_COUNT,
} from '../../../options/skill-learning.option';
import {
  THEORY_ANSWER_OPTIONS_COUNT as ANSWER_OPTIONS_COUNT,
  THEORY_DIRECTIONS_PROMPT_COPIED_KEY as DIRECTIONS_PROMPT_COPIED_KEY,
  THEORY_FIXED_PASSING_PERCENTAGE as FIXED_PASSING_PERCENTAGE,
  THEORY_FIXED_QUESTIONS_COUNT as FIXED_QUESTIONS_COUNT,
  THEORY_FIXED_TIME_LIMIT_MINUTES as FIXED_TIME_LIMIT_MINUTES,
  THEORY_MAX_TEST_BANK_QUESTIONS as MAX_TEST_BANK_QUESTIONS,
  THEORY_MIN_DESCRIPTION_LENGTH as MIN_DESCRIPTION_LENGTH,
  THEORY_MIN_TEST_BANK_QUESTIONS as MIN_TEST_BANK_QUESTIONS,
  THEORY_DESCRIPTION_CONTROL as DESCRIPTION_CONTROL,
  TheoryLevelInput,
  TheoryLevelSavePayload,
  TheoryTestGenerationPayload,
} from './theory-level-editor.option';

@Component({
  selector: 'app-skill-theory-level-editor',
  templateUrl: './theory-level-editor.component.html',
  styleUrls: ['./theory-level-editor.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillTheoryLevelEditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @ViewChild('questionList') questionList: ElementRef;
  @ViewChildren('questionInput') questionInputs: QueryList<ElementRef>;

  @Input() theoryLevel: TheoryLevelInput = null;
  @Input() levelNumber = SKILL_LEARNING_LEVEL_COUNT.ONE;
  @Input() directionsPrompt = '';
  @Input() generatedQuestions: SkillTestQuestion[] = [];
  @Input() isGeneratingTestQuestions = false;
  @Input() isSubmitting = false;

  @Output() levelSaved = new EventEmitter<TheoryLevelSavePayload>();
  @Output() testQuestionsGenerationRequested = new EventEmitter<TheoryTestGenerationPayload>();

  form: FormGroup;
  testQuestions: SkillTestQuestion[] = [];
  copyStatusKey: string | null = null;
  private pendingQuestionIndex: number | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.buildForm();
    this.testQuestions = this.hasGeneratedQuestionPayload(this.generatedQuestions)
      ? this.normalizeQuestionList(this.generatedQuestions)
      : this.extractTestQuestions(this.theoryLevel);
  }

  ngAfterViewInit(): void {
    this.questionInputs.changes
      .takeUntil(this.destroy$)
      .subscribe(() => this.focusPendingQuestion());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.theoryLevel || changes.levelNumber) {
      this.form = this.buildForm();
      this.testQuestions = this.extractTestQuestions(this.theoryLevel);
      this.copyStatusKey = null;
    }
    if (changes.generatedQuestions && this.hasGeneratedQuestionPayload(changes.generatedQuestions.currentValue)) {
      this.testQuestions = this.normalizeQuestionList(changes.generatedQuestions.currentValue);
    }
  }

  get isFormInvalid(): boolean {
    return !this.form || this.form.invalid || this.isQuestionBankInvalid || this.isGeneratingTestQuestions;
  }

  get descriptionLength(): number {
    const value: string = this.form ? this.form.get('description').value || '' : '';
    return value.length;
  }

  get minDescriptionLength(): number {
    return MIN_DESCRIPTION_LENGTH;
  }

  get requiredQuestionBankCount(): number {
    return MIN_TEST_BANK_QUESTIONS;
  }

  get maxQuestionBankCount(): number {
    return MAX_TEST_BANK_QUESTIONS;
  }

  get questionBankCount(): number {
    return this.testQuestions.length;
  }

  get isQuestionBankReady(): boolean {
    return this.questionBankCount >= MIN_TEST_BANK_QUESTIONS
      && this.questionBankCount <= MAX_TEST_BANK_QUESTIONS;
  }

  get isQuestionBankInvalid(): boolean {
    return !this.isQuestionBankReady || this.hasInvalidQuestionBankItems();
  }

  get canAddQuestion(): boolean {
    return !this.isSubmitting && !this.isGeneratingTestQuestions && this.questionBankCount < MAX_TEST_BANK_QUESTIONS;
  }

  get canGenerateTestQuestions(): boolean {
    return this.form
      && this.form.get('description').valid
      && !this.isSubmitting
      && !this.isGeneratingTestQuestions;
  }

  get canCopyDirectionsPrompt(): boolean {
    return (this.directionsPrompt || '').trim().length > 0;
  }

  onGenerateTestQuestions(): void {
    if (!this.canGenerateTestQuestions) {
      return;
    }
    this.testQuestionsGenerationRequested.emit({
      levelNumber: this.levelNumber,
      description: (this.form.get(DESCRIPTION_CONTROL).value || SKILL_LEARNING_EMPTY_TEXT).trim(),
    });
  }

  onAddQuestion(): void {
    if (!this.canAddQuestion) {
      return;
    }
    const newQuestionIndex = this.testQuestions.length;
    this.pendingQuestionIndex = newQuestionIndex;
    this.testQuestions = this.testQuestions.concat([this.createEmptyQuestion()]);
  }

  onRemoveQuestion(index: number): void {
    const question = this.testQuestions[index];
    if (!this.canRemoveQuestion(question)) {
      return;
    }
    this.testQuestions = this.testQuestions.filter((item, itemIndex) => itemIndex !== index);
  }

  onQuestionTextChange(index: number, value: string): void {
    const question = this.testQuestions[index];
    if (!question) {
      return;
    }
    question.questionText = value;
  }

  onQuestionTextInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.onQuestionTextChange(index, target.value);
  }

  onAnswerInput(questionIndex: number, answerIndex: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.onAnswerChange(questionIndex, answerIndex, target.value);
  }

  onAnswerChange(questionIndex: number, answerIndex: number, value: string): void {
    const question = this.testQuestions[questionIndex];
    if (!question || answerIndex < 0 || answerIndex >= ANSWER_OPTIONS_COUNT) {
      return;
    }
    question.answers = this.normalizeAnswers(question);
    question.answers[answerIndex] = value;
  }

  onCorrectAnswerChange(questionIndex: number, answerIndex: number): void {
    const question = this.testQuestions[questionIndex];
    if (!question) {
      return;
    }
    question.correctAnswerIndex = answerIndex;
  }

  canRemoveQuestion(question: SkillTestQuestion): boolean {
    return !!question && !this.isSubmitting && !this.isGeneratingTestQuestions;
  }

  isQuestionInvalid(question: SkillTestQuestion): boolean {
    return !this.hasQuestionText(question)
      || !this.hasValidAnswers(question)
      || !this.hasValidCorrectAnswer(question);
  }

  trackByQuestionIndex(index: number, question: SkillTestQuestion): string | number {
    return question && question.id ? question.id : index;
  }

  trackByAnswerIndex(index: number): number {
    return index;
  }

  onCopyDirectionsPrompt(): void {
    if (!this.canCopyDirectionsPrompt) {
      return;
    }
    this.copyTextToClipboard(this.directionsPrompt);
    this.copyStatusKey = DIRECTIONS_PROMPT_COPIED_KEY;
  }

  onSave(): void {
    if (this.isFormInvalid) {
      return;
    }
    const request: TheoryLevelRequest = {
      description: this.form.get(DESCRIPTION_CONTROL).value,
      topics: [],
      questionsCount: FIXED_QUESTIONS_COUNT,
      timeLimitMinutes: FIXED_TIME_LIMIT_MINUTES,
      passingPercentage: FIXED_PASSING_PERCENTAGE,
      testQuestions: this.createQuestionBankRequest(),
    };
    this.levelSaved.emit({
      levelNumber: this.levelNumber,
      request,
      documentFile: null,
    });
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      description: [
        this.theoryLevel ? this.theoryLevel.description : '',
        [Validators.required, Validators.minLength(MIN_DESCRIPTION_LENGTH)],
      ],
      questionsCount: [
        FIXED_QUESTIONS_COUNT,
        [Validators.required],
      ],
      timeLimitMinutes: [
        FIXED_TIME_LIMIT_MINUTES,
        [Validators.required],
      ],
      passingPercentage: [
        FIXED_PASSING_PERCENTAGE,
        [Validators.required],
      ],
    });
  }

  private extractTestQuestions(level: TheoryLevelInput): SkillTestQuestion[] {
    if (!level || !level.testQuestions) {
      return [];
    }
    return this.normalizeQuestionList(level.testQuestions);
  }

  private hasGeneratedQuestionPayload(value: SkillTestQuestion[]): boolean {
    return !!value && value.length > 0;
  }

  private normalizeQuestionList(questions: SkillTestQuestion[]): SkillTestQuestion[] {
    return (questions || []).map(question => this.normalizeQuestion(question));
  }

  private normalizeQuestion(question: SkillTestQuestion): SkillTestQuestion {
    return {
      id: question && question.id ? question.id : null,
      questionText: question && question.questionText ? question.questionText : '',
      answers: this.normalizeAnswers(question),
      correctAnswerIndex: this.resolveCorrectAnswerIndex(question),
    };
  }

  private normalizeAnswers(question: SkillTestQuestion): string[] {
    const source = question && question.answers ? question.answers : [];
    const answers = [];
    for (let index = 0; index < ANSWER_OPTIONS_COUNT; index += 1) {
      answers.push(source[index] || '');
    }
    return answers;
  }

  private resolveCorrectAnswerIndex(question: SkillTestQuestion): number | null {
    if (!question || typeof question.correctAnswerIndex !== 'number') {
      return null;
    }
    return question.correctAnswerIndex;
  }

  private createEmptyQuestion(): SkillTestQuestion {
    return {
      id: null,
      questionText: '',
      answers: ['', '', ''],
      correctAnswerIndex: 0,
    };
  }

  private focusPendingQuestion(): void {
    if (this.pendingQuestionIndex === null) {
      return;
    }
    this.scrollQuestionListToBottom();
    this.focusQuestionInput(this.pendingQuestionIndex);
    this.pendingQuestionIndex = null;
  }

  private scrollQuestionListToBottom(): void {
    const questionListElement = this.questionList && this.questionList.nativeElement;
    if (questionListElement) {
      questionListElement.scrollTop = questionListElement.scrollHeight;
    }
  }

  private focusQuestionInput(questionIndex: number): void {
    const inputs = this.questionInputs ? this.questionInputs.toArray() : [];
    const input = inputs[questionIndex];
    if (input && input.nativeElement) {
      input.nativeElement.focus();
    }
  }

  private createQuestionBankRequest(): SkillTestQuestion[] {
    return this.testQuestions.map(question => this.createQuestionRequest(question));
  }

  private createQuestionRequest(question: SkillTestQuestion): SkillTestQuestion {
    return {
      id: this.normalizeQuestionId(question),
      questionText: this.normalizeText(question.questionText),
      answers: this.normalizeAnswers(question).map(answer => this.normalizeText(answer)),
      correctAnswerIndex: question.correctAnswerIndex,
    };
  }

  private normalizeQuestionId(question: SkillTestQuestion): string | null {
    return question && question.id ? this.normalizeText(question.id) : null;
  }

  private hasInvalidQuestionBankItems(): boolean {
    if (this.hasDuplicateQuestionTexts()) {
      return true;
    }
    return this.testQuestions.some(question => this.isQuestionInvalid(question));
  }

  private hasDuplicateQuestionTexts(): boolean {
    const seen = {};
    for (let index = 0; index < this.testQuestions.length; index += 1) {
      const text = this.normalizeLookupText(this.testQuestions[index].questionText);
      if (text && seen[text]) {
        return true;
      }
      seen[text] = true;
    }
    return false;
  }

  private hasValidAnswers(question: SkillTestQuestion): boolean {
    const answers = this.normalizeAnswers(question);
    return answers.length === ANSWER_OPTIONS_COUNT
      && answers.every(answer => this.normalizeText(answer).length > 0)
      && !this.hasDuplicateAnswers(answers);
  }

  private hasDuplicateAnswers(answers: string[]): boolean {
    const seen = {};
    for (let index = 0; index < answers.length; index += 1) {
      const answer = this.normalizeLookupText(answers[index]);
      if (seen[answer]) {
        return true;
      }
      seen[answer] = true;
    }
    return false;
  }

  private hasValidCorrectAnswer(question: SkillTestQuestion): boolean {
    return typeof question.correctAnswerIndex === 'number'
      && question.correctAnswerIndex >= 0
      && question.correctAnswerIndex < ANSWER_OPTIONS_COUNT;
  }

  private hasQuestionText(question: SkillTestQuestion): boolean {
    return this.normalizeText(question.questionText).length > 0;
  }

  private normalizeLookupText(value: string): string {
    return this.normalizeText(value).toLowerCase();
  }

  private normalizeText(value: string): string {
    return (value || '').trim();
  }

  private copyTextToClipboard(text: string): void {
    const onCopy = (event: ClipboardEvent) => {
      event.clipboardData.setData('text/plain', text);
      event.preventDefault();
      document.removeEventListener('copy', onCopy);
    };
    document.addEventListener('copy', onCopy);
    document.execCommand('copy');
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/theory-level-editor/theory-level-editor.component.ts